import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { DriverData } from './types';
import { baseUrl } from '../../customDataProvider';

interface AccountabilityTabProps {
  driverData: DriverData | null;
  onRefresh?: () => void;
  isLoading?: boolean;
}

interface FileType {
  id: number;
  name: string;
  longDescription?: string;
  active: boolean;
}

const isImageFile = (mimeType?: string, filename?: string) => {
  if (mimeType?.startsWith('image/')) return true;
  if (filename && /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename)) return true;
  return false;
};

const isPdfFile = (mimeType?: string, filename?: string) => {
  if (mimeType === 'application/pdf') return true;
  if (filename && filename.toLowerCase().endsWith('.pdf')) return true;
  return false;
};

export const AccountabilityTab: React.FC<AccountabilityTabProps> = ({ driverData, onRefresh, isLoading = false }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [fileTypes, setFileTypes] = useState<FileType[]>([]);
  const [isLoadingFileTypes, setIsLoadingFileTypes] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFileTypeId, setSelectedFileTypeId] = useState<number | ''>('');
  const [selectedExpiryDate, setSelectedExpiryDate] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch file types on component mount
  useEffect(() => {
    const fetchFileTypes = async () => {
      try {
        setIsLoadingFileTypes(true);
        const result = await dataProvider.getList('code-tables/accountability-file-types', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { active: true },
        });
        setFileTypes(result.data || []);
      } catch (error) {
        console.error('Error fetching file types:', error);
        notify('Error loading file types', { type: 'error' });
      } finally {
        setIsLoadingFileTypes(false);
      }
    };

    fetchFileTypes();
  }, [dataProvider, notify]);

  if (!driverData) {
    return <Typography>Loading...</Typography>;
  }

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!selectedFileTypeId) {
      notify('Please select a file type', { type: 'error' });
      return;
    }

    const file = files[0];

    try {
      setIsUploading(true);

      const fileResult = await dataProvider.create('files', {
        data: {
          file,
          name: file.name,
          folder: 'driver-accountability',
          description: `Accountability file for driver ${driverData?.id}`,
        },
      });

      const uploadedFileId = fileResult.data?.id;
      if (!uploadedFileId) {
        throw new Error('File upload failed');
      }

      await dataProvider.create('drivers/files', {
        data: {
          driverId: driverData.id,
          fileId: uploadedFileId,
          fileTypeId: selectedFileTypeId,
          expiryDate: selectedExpiryDate || undefined,
        },
      });

      notify('File uploaded successfully', { type: 'success' });

      // Reset dialog state
      setUploadDialogOpen(false);
      setSelectedFileTypeId('');
      setSelectedExpiryDate('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onRefresh?.();
    } catch (error: any) {
      notify('Error uploading file: ' + (error.message || 'Unknown error'), { type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (driverFile: any) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await dataProvider.delete('drivers/files', { id: driverFile.id });
      notify('File deleted successfully', { type: 'success' });
      onRefresh?.();
    } catch (error: any) {
      notify('Error deleting file: ' + (error.message || 'Unknown error'), { type: 'error' });
    }
  };

  const handleViewFile = (driverFile: any) => {
    console.log('Opening file viewer for:', driverFile);
    setSelectedFile(driverFile);
    setViewerOpen(true);
  };

  const handleDownloadFile = () => {
    if (!selectedFile?.file?.id) return;
    
    const token = localStorage.getItem('accessToken');
    const url = `${baseUrl}/files/${selectedFile.file.id}/content`;
    
    fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = selectedFile.file.name || `file-${selectedFile.file.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Download error:', error);
        notify('Failed to download file', { type: 'error' });
      });
  };

  // Format expiry date for display
  const formatExpiryDate = (date?: Date) => {
    if (!date) return null;
    const expiryDate = new Date(date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    const formatted = expiryDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    if (daysUntilExpiry <= 0) {
      return { formatted, status: 'expired', daysLeft: 0 };
    } else if (daysUntilExpiry <= 30) {
      return { formatted, status: 'expiring', daysLeft: daysUntilExpiry };
    } else {
      return { formatted, status: 'active', daysLeft: daysUntilExpiry };
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Accountability Files
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          disabled={isLoadingFileTypes}
        >
          Add File
        </Button>
      </Box>

      {isLoadingFileTypes ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : driverData.files && driverData.files.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
          }}
        >
          {driverData.files.map((driverFile: any) => {
            const fileTypeName = driverFile.fileType?.name || 'Unknown Type';
            const expiryInfo = formatExpiryDate(driverFile.expiryDate);
            const isExpiring = expiryInfo?.status === 'expiring';
            const isExpired = expiryInfo?.status === 'expired';

            return (
              <Paper
                key={driverFile.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: isExpired ? '2px solid #d32f2f' : isExpiring ? '2px solid #f57c00' : '1px solid #e0e0e0',
                  bgcolor: '#f8f9fa',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
                  },
                }}
              >
                {/* File Preview */}
                {isImageFile(driverFile.file?.mimeType, driverFile.file?.name) ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                    onClick={() => handleViewFile(driverFile)}
                  >
                    <img
                      src={driverFile.file?.url}
                      alt={driverFile.file?.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                      onError={(e) => console.error('Image load error:', e)}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      borderRadius: 1,
                      backgroundColor: '#e3f2fd',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                    onClick={() => handleViewFile(driverFile)}
                  >
                    <FileIcon sx={{ fontSize: 36, color: '#1976d2', mb: 0.5 }} />
                    <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500, textAlign: 'center' }}>
                      {driverFile.file?.name?.split('.').pop()?.toUpperCase() || 'FILE'}
                    </Typography>
                  </Box>
                )}

                {/* File Metadata */}
                <Box sx={{ flex: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem', mb: 0.5 }}>
                    {fileTypeName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {driverFile.file?.name}
                  </Typography>

                  {expiryInfo && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: isExpired ? '#d32f2f' : isExpiring ? '#f57c00' : '#1976d2',
                        fontWeight: isExpired || isExpiring ? 600 : 400,
                      }}
                    >
                      {isExpired
                        ? `Expired on ${expiryInfo.formatted}`
                        : isExpiring
                        ? `Expires in ${expiryInfo.daysLeft} days`
                        : `Expires ${expiryInfo.formatted}`
                      }
                    </Typography>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleViewFile(driverFile)}
                    color="primary"
                    title="View"
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleDownloadFile}
                    color="primary"
                    title="Download"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteFile(driverFile)}
                    color="error"
                    title="Delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#fafafa',
            border: '1px dashed #e0e0e0',
          }}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            No accountability files yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Add File" to upload your first accountability file
          </Typography>
        </Paper>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Accountability File</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>File Type *</InputLabel>
            <Select
              value={selectedFileTypeId}
              label="File Type *"
              onChange={(e) => setSelectedFileTypeId(e.target.value as number)}
              disabled={isUploading}
            >
              {fileTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Expiry Date (Optional)"
            type="date"
            value={selectedExpiryDate}
            onChange={(e) => setSelectedExpiryDate(e.target.value)}
            disabled={isUploading}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
          />

          <Box
            sx={{
              border: '2px dashed #1976d2',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              cursor: isUploading ? 'wait' : 'pointer',
              bgcolor: '#f5f5f5',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: isUploading ? '#f5f5f5' : '#e3f2fd',
              },
              mb: 2,
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileInputChange}
              disabled={isUploading}
              style={{ display: 'none' }}
            />
            <UploadIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Click to select file
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF, DOC, DOCX, JPG, JPEG, PNG
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFileTypeId('');
                setSelectedExpiryDate('');
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || !selectedFileTypeId}
            >
              {isUploading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {isUploading ? 'Uploading...' : 'Select & Upload'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setSelectedFile(null);
        }}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#000',
            backgroundImage: 'none',
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => setViewerOpen(false)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#fff',
            zIndex: 1400,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100vh',
            backgroundColor: '#000',
            padding: 2,
            paddingBottom: 10,
          }}
        >
          {selectedFile && isImageFile(selectedFile.file?.mimeType, selectedFile.file?.name) ? (
            <img
              src={selectedFile.file?.url}
              alt={selectedFile.file?.name}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 120px)',
                objectFit: 'contain',
              }}
              onError={(e) => {
                console.error('Image load error:', e);
              }}
              onLoad={() => {
                console.log('Image loaded successfully');
              }}
            />
          ) : selectedFile && isPdfFile(selectedFile.file?.mimeType, selectedFile.file?.name) ? (
            <iframe
              src={selectedFile.file?.url}
              style={{
                width: '100%',
                height: 'calc(100vh - 120px)',
                border: 'none',
              }}
              title={selectedFile.file?.name}
              onLoad={() => {
                console.log('PDF iframe loaded');
              }}
              onError={() => console.error('PDF iframe load error')}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: '#fff' }}>
              <FileIcon sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Preview not available
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                {selectedFile?.file?.name}
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadFile}
              >
                Download File
              </Button>
            </Box>
          )}
        </Box>

        {/* Bottom Toolbar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            zIndex: 1200,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadFile}
          >
            Download
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setViewerOpen(false)}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};
